const BiopharmaceuticsUnit1Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit I: Introduction to Biopharmaceutics, Absorption & Distribution</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            <strong>Biopharmaceutics</strong> is the study of how the physicochemical properties of drugs, the dosage form, and the route of administration affect the rate and extent of drug absorption into the systemic circulation.
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Drug Absorption</h3>
        <p class="text-[13px] text-slate-700 mb-4">Absorption is the process of movement of an unchanged drug from its site of administration to the systemic circulation.</p>
        
        <h4 class="text-[16px] font-bold text-blue-900 mb-3">A. Mechanisms of Drug Absorption through GIT</h4>
        <div class="overflow-x-auto rounded-lg border border-slate-200 mb-6">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-blue-50 text-blue-900">
                    <tr>
                        <th class="px-4 py-3 font-bold w-1/4">Mechanism</th>
                        <th class="px-4 py-3 font-bold w-3/4">Characteristics & Description</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-700">
                    <tr>
                        <td class="px-4 py-3 font-bold">Passive Diffusion</td>
                        <td class="px-4 py-3">Major process (accounts for >90% of drugs). Movement occurs <strong>along the concentration gradient</strong> (High to Low). Requires no energy. Best for lipid-soluble, unionized drugs. Follows Fick's First Law.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Active Transport</td>
                        <td class="px-4 py-3">Movement occurs <strong>against the concentration gradient</strong> (Low to High). Requires <strong>ATP (energy)</strong> and specific carrier proteins. Can be saturated and competitively inhibited. Examples: Levodopa, Iron.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Facilitated Diffusion</td>
                        <td class="px-4 py-3">Carrier-mediated but occurs <strong>along the concentration gradient</strong>. Requires no energy. Faster than passive diffusion. Example: Absorption of Vitamin B12.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Pore Transport</td>
                        <td class="px-4 py-3">Also called convective transport. Very small (<100 Daltons), water-soluble molecules (like Urea, Water) pass through aqueous pores in the cell membrane.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Pinocytosis</td>
                        <td class="px-4 py-3">"Cell-drinking". Vesicular transport for macromolecular drugs (e.g., Sabin polio vaccine, fat-soluble vitamins) by engulfing them into the cell membrane.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <h4 class="text-[16px] font-bold text-blue-900 mb-3">B. Factors Influencing Drug Absorption</h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h5 class="font-bold text-blue-800 text-[14px] mb-2">Physicochemical Factors</h5>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Drug Solubility & Dissolution Rate:</strong> (Noyes-Whitney equation). Poorly soluble drugs have low absorption.</li>
                    <li><strong>Particle Size:</strong> Smaller size = larger surface area = faster dissolution (e.g., Griseofulvin).</li>
                    <li><strong>Lipophilicity & pKa:</strong> Unionized, lipid-soluble drugs are absorbed faster (pH-partition hypothesis).</li>
                </ul>
            </div>
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h5 class="font-bold text-blue-800 text-[14px] mb-2">Pharmaceutical Factors</h5>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Dosage Form:</strong> Solution > Suspension > Capsule > Tablet > Coated Tablet.</li>
                    <li><strong>Excipients:</strong> Binders can delay, whereas disintegrants can enhance dissolution and absorption.</li>
                    <li><strong>Manufacturing Variables:</strong> Compression force affects tablet hardness and disintegration time.</li>
                </ul>
            </div>
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h5 class="font-bold text-blue-800 text-[14px] mb-2">Patient Related Factors</h5>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Gastric Emptying Time:</strong> Rapid emptying favors faster absorption in the intestine.</li>
                    <li><strong>Intestinal Transit Time:</strong> Diarrhea decreases transit time, reducing absorption.</li>
                    <li><strong>Food Presence:</strong> Can delay (e.g., Tetracycline with milk) or enhance (e.g., Griseofulvin with fatty meals) absorption.</li>
                </ul>
            </div>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">2. Drug Distribution</h3>
        <p class="text-[13px] text-slate-700 mb-4">Distribution is the reversible transfer of a drug between the systemic circulation and extravascular fluids/tissues.</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div class="border border-blue-100 p-4 rounded-lg bg-blue-50">
                <h4 class="text-[15px] font-bold text-blue-900 mb-2">Apparent Volume of Distribution (V<sub>d</sub>)</h4>
                <p class="text-[13px] text-slate-700 mb-2">It is a hypothetical volume of body fluid into which a drug is dissolved/distributed at a concentration equal to that in plasma.</p>
                <div class="bg-white p-2 rounded border border-blue-200 text-center font-mono text-[14px] font-bold text-blue-800 mb-2">
                    V<sub>d</sub> = Amount of drug in body (X) / Plasma Concentration (C)
                </div>
                <p class="text-[12px] text-slate-600"><em>High V<sub>d</sub> indicates extensive tissue binding (e.g., Chloroquine). Low V<sub>d</sub> indicates high plasma protein binding.</em></p>
            </div>

            <div class="border border-blue-100 p-4 rounded-lg bg-blue-50">
                <h4 class="text-[15px] font-bold text-blue-900 mb-2">Physiological Barriers</h4>
                <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-700">
                    <li><strong>Blood-Brain Barrier (BBB):</strong> Tight junctions in brain capillaries. Only highly lipophilic drugs (e.g., Thiopental) can cross easily.</li>
                    <li><strong>Placental Barrier:</strong> Not a strict barrier; many lipophilic drugs and small molecules easily cross from mother to fetus, risking teratogenicity.</li>
                </ul>
            </div>
        </div>

        <div>
            <h4 class="text-[16px] font-bold text-blue-900 mb-3">Protein Binding of Drugs</h4>
            <p class="text-[13px] text-slate-700 mb-3">Drugs bind reversibly to plasma proteins. <strong>Only the unbound (free) drug is pharmacologically active</strong>, can cross membranes, and be metabolized/excreted.</p>
            
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                    <h5 class="font-bold text-slate-800 text-[14px] mb-1">Human Serum Albumin (HSA)</h5>
                    <p class="text-[12px] text-slate-600">Most abundant protein. Has high affinity for <strong>Weakly Acidic Drugs</strong>. <br><em>Examples:</em> Salicylates, Penicillins, Warfarin, Phenytoin.</p>
                </div>
                <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                    <h5 class="font-bold text-slate-800 text-[14px] mb-1">Alpha-1-Acid Glycoprotein (AAG)</h5>
                    <p class="text-[12px] text-slate-600">An acute-phase reactant protein. Binds primarily to <strong>Weakly Basic Drugs</strong>. <br><em>Examples:</em> Propranolol, Lignocaine, Imipramine.</p>
                </div>
            </div>

            <div class="bg-red-50 p-4 border border-red-200 rounded-lg">
                <h5 class="font-bold text-red-900 text-[14px] mb-2">Clinical Significance & Kinetics of Protein Binding</h5>
                <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                    <li><strong>Displacement Interactions:</strong> When two drugs compete for the same binding site, the one with higher affinity displaces the other. This increases the "free" concentration of the displaced drug, potentially causing <strong>toxicity</strong>. <br><em>Example:</em> Aspirin (high affinity) displaces Warfarin, causing hemorrhage.</li>
                    <li><strong>Half-Life Extension:</strong> Protein-bound drugs act as a temporary reservoir, slowly releasing the free drug, thereby prolonging its half-life and duration of action.</li>
                    <li><strong>Kinetics:</strong> Protein binding follows the <em>Law of Mass Action</em>. At high drug concentrations, the protein binding sites can become saturated (Non-linear kinetics).</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default BiopharmaceuticsUnit1Notes;